import React, { useState, useEffect, useMemo } from 'react';
import ChampSelect from './ui/ChampSelect';
import GroupedClassDropdown from './ui/GroupedClassDropdown';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Toast } from './ui/Toast';
import {
  Lock as LockIcon,
  Unlock,
  Shield,
  Clock,
  Search,
  AlertTriangle,
  CheckCircle,
  Users,
  User,
  History,
  Settings,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Save,
  X, ArrowLeft} from 'lucide-react';
import useVerrouillageNotes from '../hooks/useVerrouillageNotes';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';

const ITEMS_PER_PAGE = 10;

const ParametresVerrouillageNotes = ({ onBackToDashboard, embedded = false }) => {
  const {
    config, classes, etatClasse, historique, pagination,
    loading, error, chargerConfig, modifierDelai,
    chargerEtatClasse, deverrouiller, chargerHistorique
  } = useVerrouillageNotes();

  const { hasGranularPermission } = usePermissions();

  const [activeTab, setActiveTab] = useState('deverrouillage');
  const [activeYear, setActiveYear] = useState(null);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedTrimestre, setSelectedTrimestre] = useState('');
  const [selectedMoisId, setSelectedMoisId] = useState('');
  const [moisScolaires, setMoisScolaires] = useState([]);
  const [isClasseDropdownOpen, setIsClasseDropdownOpen] = useState(false);
  const [classeSearchTerm, setClasseSearchTerm] = useState('');
  const [eleveSearchTerm, setEleveSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalEleveId, setModalEleveId] = useState(null);
  const [modalEleveNom, setModalEleveNom] = useState('');
  const [motif, setMotif] = useState('');
  const [expandedHistorique, setExpandedHistorique] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => setToast({ type, message });

  // Chargements initiaux
  useEffect(() => {
    const chargerAnnee = async () => {
      try {
        const result = await api.anneesScolaires.getActive();
        if (result.success && result.data) setActiveYear(result.data);
      } catch (err) { console.error('Erreur chargement année:', err); }
    };
    chargerAnnee();
  }, []);

  useEffect(() => {
    const chargerMois = async () => {
      if (!activeYear) return;
      try {
        const result = await api.moisScolaires.getAll();
        let mois = result.success ? result.data : result;
        if (activeYear.annee) mois = mois.filter(m => m.annee_scolaire === activeYear.annee);
        setMoisScolaires(mois);
      } catch (err) { console.error('Erreur chargement mois:', err); }
    };
    chargerMois();
  }, [activeYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isClasseDropdownOpen && !event.target.closest('.classe-dropdown-container')) {
        setIsClasseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isClasseDropdownOpen]);

  useEffect(() => {
    if (selectedClasse) {
      const classe = classes.find(c => c.id.toString() === selectedClasse);
      if (classe) setSelectedSection(classe.section_nom?.toLowerCase() || '');
    }
  }, [selectedClasse, classes]);

  useEffect(() => { setCurrentPage(1); }, [eleveSearchTerm]);

  // Handlers
  const handleChargerVerrouillages = async () => {
    if (!selectedClasse || !activeYear) return;
    const options = {};
    if (selectedSection === 'primaire' && selectedMoisId) options.mois_id = selectedMoisId;
    else if (selectedTrimestre) options.trimestre = selectedTrimestre;
    setEleveSearchTerm('');
    setCurrentPage(1);
    await chargerEtatClasse(selectedClasse, activeYear.id, options);
  };

  const handleClasseSelect = (classe) => {
    setSelectedClasse(classe.id.toString());
    setClasseSearchTerm('');
    setIsClasseDropdownOpen(false);
    setSelectedTrimestre('');
    setSelectedMoisId('');
  };

  const openModalDeverrouillage = (type, eleveId = null, eleveNom = '') => {
    setModalType(type);
    setModalEleveId(eleveId);
    setModalEleveNom(eleveNom);
    setMotif('');
    setShowModal(true);
  };

  const confirmerDeverrouillage = async () => {
    if (motif.length < 10) {
      showToast('error', 'Le motif doit contenir au moins 10 caractères');
      return;
    }
    const params = {
      classe_id: parseInt(selectedClasse),
      annee_scolaire_id: activeYear.id,
      motif, type: modalType,
    };
    if (modalType === 'eleve' && modalEleveId) params.eleve_id = modalEleveId;
    if (selectedSection === 'primaire' && selectedMoisId) params.mois_id = parseInt(selectedMoisId);
    else if (selectedTrimestre) params.trimestre = selectedTrimestre;

    const result = await deverrouiller(params);
    if (result.success) {
      showToast('success', result.message);
      setShowModal(false);
      await handleChargerVerrouillages();
    } else {
      showToast('error', result.error || 'Erreur lors du déverrouillage');
    }
  };

  const handleSaveConfig = async (configId, newDelai) => {
    const result = await modifierDelai(configId, newDelai);
    if (result.success) showToast('success', 'Délai mis à jour avec succès');
    else showToast('error', result.error || 'Erreur lors de la mise à jour');
  };

  // Filtrage + Pagination
  const elevesFiltres = useMemo(() => {
    if (!etatClasse?.eleves) return [];
    if (!eleveSearchTerm.trim()) return etatClasse.eleves;
    const search = eleveSearchTerm.toLowerCase().trim();
    return etatClasse.eleves.filter(e => `${e.nom} ${e.prenom}`.toLowerCase().includes(search));
  }, [etatClasse?.eleves, eleveSearchTerm]);

  const totalPages = Math.ceil(elevesFiltres.length / ITEMS_PER_PAGE);
  const elevesPagines = elevesFiltres.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // `GroupedClassDropdown` groupe par section ; le hook ne renvoie que les
  // classes, on en deduit les sections presentes.
  //
  // Une classe sans section est volontairement ignoree ici : le composant tient
  // deja son propre groupe « Sans section ». Lui en fournir un second la faisait
  // apparaitre DEUX fois dans la liste — une fois dans le groupe qu'on venait
  // d'inventer, une fois dans le sien.
  const sectionsDesClasses = React.useMemo(() => {
    const vues = new Map();
    classes.forEach((c) => {
      if (!c.section_id) return;
      if (!vues.has(c.section_id)) {
        vues.set(c.section_id, { id: c.section_id, nom: c.section_nom, ordre: c.section_ordre });
      }
    });
    return [...vues.values()];
  }, [classes]);

  const filteredClasses = classes
    .filter(c => c.nom.toLowerCase().includes(classeSearchTerm.toLowerCase()))
    .sort((a, b) => {
      if (a.section_nom !== b.section_nom) return (a.section_nom || '').localeCompare(b.section_nom || '');
      return a.nom.localeCompare(b.nom);
    });

  const classeSelectionnee = classes.find(c => c.id.toString() === selectedClasse);

  // ========================================
  // RENDU
  // ========================================
  return (
    <div className="w-full">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}


      {/* Header */}
    {/* Header - masqué en mode embedded */}
    {!embedded && (
    <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
        <Button onClick={onBackToDashboard} variant="outline" size="sm"
            className="border-[#246990] text-[#246990] hover:bg-[#246990] hover:text-white">
            <ArrowLeft size={15} className="mr-1.5" /> Retour
        </Button>
        <div className="w-8 h-8 bg-[#246990] rounded-lg flex items-center justify-center">
            <LockIcon className="text-white" size={16} />
        </div>
        <div>
            <h1 className="text-xl font-bold text-gray-900">Déverrouillage de Notes</h1>
            <p className="text-sm text-gray-600">Déverrouiller les notes, configurer les délais de réclamation</p>
        </div>
        </div>
    </div>
    )}


      {/* Onglets */}
      <div className="flex gap-3 mb-6 border-b border-gray-200 pb-3">
        {[
        { id: 'deverrouillage', label: 'Déverrouillage', icon: Unlock, permission: 'UNLOCK' },
        { id: 'config', label: 'Configuration des délais', icon: Settings, permission: 'CONFIG' },
        { id: 'historique', label: 'Historique', icon: History, permission: 'HISTORY' }
        ].filter(tab => hasGranularPermission('NOTES', 'VERROUILLAGE', tab.permission)).map(tab => (
          <button key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 'historique') chargerHistorique({ annee_scolaire_id: activeYear?.id });
            }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-[#246990] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ============ ONGLET DÉVERROUILLAGE ============ */}
      {activeTab === 'deverrouillage' && (
        <div className="space-y-6">
          {/* Filtres */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-5">Rechercher les notes verrouillées</h3>
              {/* Trois hauteurs differentes se suivaient : le selecteur de
                  classe a 44 px, le select de periode a 36, le bouton a 42 —
                  et le bouton occupait un tiers de la ligne en `w-full`, d'ou
                  sa taille sans rapport avec ce qu'il fait.
                  `auto` sur la troisieme colonne le laisse prendre sa largeur,
                  et les trois elements partagent la hauteur `h-9` de la charte. */}
              <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                {/* Classe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Classe <span className="text-red-500">*</span></label>
                  {/* `GroupedClassDropdown` existait deja et sert dans trois
                      autres ecrans : recherche, groupement par section, meme
                      apparence partout. Cette page avait reconstruit le sien,
                      avec sa propre gestion du clic exterieur. */}
                  <GroupedClassDropdown
                    value={selectedClasse}
                    classes={classes}
                    sections={sectionsDesClasses}
                    hauteur="h-9"
                    onChange={(id) => {
                      const classe = classes.find(c => String(c.id) === String(id));
                      if (classe) handleClasseSelect(classe);
                    }}
                  />
                </div>

                {/* Période */}
                {selectedSection === 'primaire' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Mois</label>
                    <ChampSelect
                      valeur={selectedMoisId}
                      onChange={setSelectedMoisId}
                      placeholder="Tous les mois"
                      options={[...moisScolaires]
                        .sort((a, b) => a.ordre - b.ordre)
                        .map(mois => ({ valeur: mois.id, libelle: `${mois.nom} (T${mois.trimestre})` }))}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Trimestre</label>
                    <ChampSelect
                      valeur={selectedTrimestre}
                      onChange={setSelectedTrimestre}
                      placeholder="Tous les trimestres"
                      options={[
                        { valeur: '1', libelle: 'Trimestre 1' },
                        { valeur: '2', libelle: 'Trimestre 2' },
                        { valeur: '3', libelle: 'Trimestre 3' },
                      ]}
                    />
                  </div>
                )}

                {/* Bouton — largeur fixe, pas une colonne entiere */}
                <Button
                  onClick={handleChargerVerrouillages}
                  disabled={!selectedClasse || loading}
                  className="h-9 w-full justify-center gap-2 bg-[#246990] text-white hover:bg-[#1a4d6e] disabled:opacity-50 md:w-[150px]"
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Chargement…
                    </>
                  ) : (
                    <>
                      <Search size={16} /> Rechercher
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Résultats */}
          {etatClasse && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                {/* Barre résumé */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                      <LockIcon size={20} className="text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{etatClasse.classe?.classe_nom}</h3>
                      <p className="text-sm text-gray-500">
                        {etatClasse.total_notes_verrouillees} note(s) verrouillée(s) · Délai : {etatClasse.delai_jours} jours
                      </p>
                    </div>
                  </div>
                  {etatClasse.eleves?.length > 0 && (
                    <Button onClick={() => openModalDeverrouillage('classe')}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5">
                      <Unlock size={16} className="mr-2" /> Déverrouiller toute la classe
                    </Button>
                  )}
                </div>

                {/* Recherche élève */}
                {etatClasse.eleves?.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input type="text" placeholder="Rechercher un élève par nom..."
                        value={eleveSearchTerm} onChange={(e) => setEleveSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none transition-all"
                      />
                    </div>
                    <span className="text-sm text-gray-500">{elevesFiltres.length} élève(s)</span>
                  </div>
                )}

                {/* Tableau élèves */}
                {elevesPagines.length > 0 ? (
                  <>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Élève</th>
                            <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes verrouillées</th>
                            <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Délai</th>
                            <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {elevesPagines.map((eleve, idx) => (
                            <tr key={eleve.eleve_id}
                              className={`border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 bg-[#246990]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <User size={16} className="text-[#246990]" />
                                  </div>
                                  <span className="font-medium text-gray-900 text-sm">{eleve.nom} {eleve.prenom}</span>
                                </div>
                              </td>
                              <td className="px-5 py-3.5 text-center">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-semibold">
                                  <LockIcon size={12} /> {eleve.nb_notes_verrouillees} note(s)
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-center">
                                {eleve.toutes_dans_delai ? (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">
                                    <CheckCircle size={12} /> Dans le délai
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">
                                    <AlertTriangle size={12} /> Hors délai
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3.5 text-right">
                                <Button onClick={() => openModalDeverrouillage('eleve', eleve.eleve_id, `${eleve.nom} ${eleve.prenom}`)}
                                  className="bg-[#246990] hover:bg-[#1a4d6e] text-white px-4 py-2 text-sm">
                                  <Unlock size={14} className="mr-1.5" /> Déverrouiller
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-500">Page {currentPage} sur {totalPages} · {elevesFiltres.length} élève(s)</p>
                        <div className="flex items-center gap-2">
                          <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                            variant="outline" size="sm" className="px-3 py-1.5 text-sm disabled:opacity-40">
                            <ChevronLeft size={16} className="mr-1" /> Précédent
                          </Button>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let page;
                            if (totalPages <= 5) page = i + 1;
                            else if (currentPage <= 3) page = i + 1;
                            else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                            else page = currentPage - 2 + i;
                            return (
                              <button key={page} onClick={() => setCurrentPage(page)}
                                className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                                  currentPage === page ? 'bg-[#246990] text-white' : 'text-gray-600 hover:bg-gray-100'
                                }`}>{page}</button>
                            );
                          })}
                          <Button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                            variant="outline" size="sm" className="px-3 py-1.5 text-sm disabled:opacity-40">
                            Suivant <ChevronRight size={16} className="ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : etatClasse.eleves?.length === 0 ? (
                  <div className="text-center py-12">
                    <Unlock size={44} className="mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-700 mb-1">Aucune note verrouillée</p>
                    <p className="text-sm text-gray-500">Toutes les notes de cette classe sont déjà accessibles</p>
                  </div>
                ) : elevesFiltres.length === 0 && eleveSearchTerm ? (
                  <div className="text-center py-12">
                    <Search size={44} className="mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-700 mb-1">Aucun résultat</p>
                    <p className="text-sm text-gray-500">Aucun élève ne correspond à « {eleveSearchTerm} »</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* État initial */}
          {!etatClasse && !loading && (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 bg-[#246990]/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  <LockIcon size={28} className="text-[#246990]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Sélectionnez une classe</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto">
                  Choisissez une classe puis cliquez sur « Rechercher » pour voir les notes verrouillées.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ============ ONGLET CONFIGURATION ============ */}
      {activeTab === 'config' && (
        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-1">Délais de réclamation par section</h3>
              <p className="text-sm text-gray-500 mb-6">
                Nombre de jours après verrouillage pendant lesquels les administrateurs peuvent déverrouiller les notes.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {config.map(cfg => <ConfigDelaiCard key={cfg.id} config={cfg} onSave={handleSaveConfig} loading={loading} />)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-blue-50/50">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield size={18} className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Règle de sécurité</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Le délai maximum configurable est de <strong>14 jours</strong> — le serveur refuse
                    toute valeur au-delà. Passé ce délai, déverrouiller une note demande le droit
                    <strong className="text-blue-800">Forcer le déverrouillage hors délai</strong>,
                    qui s'accorde depuis Outils administratifs → Rôles et permissions.
                    L'opération est alors enregistrée comme forcée dans l'historique.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ ONGLET HISTORIQUE ============ */}
      {activeTab === 'historique' && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-900">Historique des déverrouillages</h3>
              {pagination.total > 0 && <span className="text-sm text-gray-500">{pagination.total} opération(s)</span>}
            </div>

            {historique.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="w-14 px-5 py-3"></th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Déverrouillage
                      </th>
                      <th className="w-20 px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Notes
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Classe
                      </th>
                      <th className="w-24 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Forcé
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Par
                      </th>
                      <th className="w-48 px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Date
                      </th>
                      <th className="w-36 px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Statut
                      </th>
                      <th className="w-12 px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {historique.map((entry, index) => {
                      const reverrouille = entry.statut === 'reverrouille';
                      const ouvert = Boolean(expandedHistorique[entry.id]);
                      const basculer = () =>
                        setExpandedHistorique(prev => ({ ...prev, [entry.id]: !prev[entry.id] }));

                      return (
                        <React.Fragment key={entry.id}>
                          <tr
                            onClick={basculer}
                            className={`cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50/50 ${
                              ouvert ? 'bg-gray-50/60' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                            }`}
                          >
                            <td className="px-5 py-3.5">
                              <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                                reverrouille ? 'bg-green-50' : 'bg-orange-50'
                              }`}>
                                {reverrouille
                                  ? <LockIcon size={16} className="text-green-600" />
                                  : <Unlock size={16} className="text-orange-600" />}
                              </div>
                            </td>

                            <td className="px-5 py-3.5">
                              <span className="text-sm font-medium text-gray-900">
                                {entry.eleve_nom ? `${entry.eleve_prenom} ${entry.eleve_nom}` : 'Classe entière'}
                              </span>
                            </td>

                            {/* Sa colonne, comme le badge : accole au nom, il se
                                decalait a chaque ligne selon sa longueur. */}
                            <td className="px-5 py-3.5 text-right text-sm text-gray-600">
                              {entry.nb_notes > 0
                                ? entry.nb_notes
                                : <span className="text-xs text-gray-300">—</span>}
                            </td>

                            <td className="px-5 py-3.5 text-sm text-gray-600">{entry.classe_nom}</td>

                            {/* Sa propre colonne : accole au nom, le badge se
                                decalait a chaque ligne selon sa longueur. */}
                            <td className="px-5 py-3.5 text-center">
                              {entry.est_force === 1 ? (
                                <span className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
                                  <Shield size={10} /> Forcé
                                </span>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>

                            <td className="px-5 py-3.5 text-sm text-gray-600">
                              {entry.employe_prenom} {entry.employe_nom}
                            </td>

                            <td className="px-5 py-3.5 text-sm text-gray-600">
                              {new Date(entry.date_deverrouillage).toLocaleDateString('fr-FR', {
                                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </td>

                            <td className="px-5 py-3.5 text-right">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                                reverrouille ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'
                              }`}>
                                {reverrouille ? 'Reverrouillé' : 'Déverrouillé'}
                              </span>
                            </td>

                            <td className="px-5 py-3.5 text-right">
                              <ChevronDown
                                size={18}
                                className={`text-gray-400 transition-transform ${ouvert ? 'rotate-180' : ''}`}
                              />
                            </td>
                          </tr>

                          {/* Le depliage : une ligne de plus, sur toute la largeur. */}
                          {ouvert && (
                            <tr className="border-b border-gray-100 bg-gray-50">
                              <td colSpan={9} className="px-5 py-4">
                                <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                                  <div>
                                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                      Motif
                                    </p>
                                    <p className="text-gray-800">{entry.motif || '—'}</p>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-500">Type</span>
                                      <span className="font-semibold text-gray-900">
                                        {entry.type_deverrouillage === 'classe' ? 'Classe entière' : 'Élève individuel'}
                                      </span>
                                    </div>
                                    {entry.trimestre && (
                                      <div className="flex justify-between gap-4">
                                        <span className="text-gray-500">Trimestre</span>
                                        <span className="font-semibold text-gray-900">Trimestre {entry.trimestre}</span>
                                      </div>
                                    )}
                                    {entry.date_reverrouillage && (
                                      <div className="flex justify-between gap-4">
                                        <span className="text-gray-500">Reverrouillé le</span>
                                        <span className="font-semibold text-gray-900">
                                          {new Date(entry.date_reverrouillage).toLocaleDateString('fr-FR', {
                                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                          })}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <History size={44} className="mx-auto mb-4 text-gray-300" />
                <p className="font-medium text-gray-700 mb-1">Aucun historique</p>
                <p className="text-sm text-gray-500">Les opérations de déverrouillage apparaîtront ici</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ MODAL DÉVERROUILLAGE ============ */}
      {/* Le modal etait un calque monte a la main. CLAUDE.md impose les
          primitives `Dialog` : Radix pose `pointer-events: none` sur le body
          tant qu'un Dialog est ouvert, et un overlay maison rendu hors de son
          portail s'affiche mais ne recoit aucun clic. */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-500 text-white">
              <Unlock size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Confirmer le déverrouillage
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {modalType === 'classe'
                  ? `Toute la classe — ${classeSelectionnee?.nom || ''}`
                  : modalEleveNom}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-amber-600" />
              <p className="text-sm leading-relaxed text-amber-800">
                {modalType === 'classe'
                  ? "Toutes les notes verrouillées de cette classe redeviennent modifiables jusqu'à la prochaine sauvegarde."
                  : `Les notes de ${modalEleveNom} redeviennent modifiables.`}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 rounded-lg border border-gray-200 p-4 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Classe</span>
                  <span className="font-semibold text-gray-900">{classeSelectionnee?.nom}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Portée</span>
                  <span className="font-semibold text-gray-900">
                    {modalType === 'classe' ? 'Classe entière' : 'Élève'}
                  </span>
                </div>
                {selectedSection === 'primaire' && selectedMoisId && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Mois</span>
                    <span className="font-semibold text-gray-900">
                      {moisScolaires.find(m => m.id.toString() === selectedMoisId)?.nom}
                    </span>
                  </div>
                )}
                {selectedTrimestre && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Trimestre</span>
                    <span className="font-semibold text-gray-900">Trimestre {selectedTrimestre}</span>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm text-gray-700">Motif du déverrouillage *</Label>
                <Textarea
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                  placeholder="Réclamation du parent — erreur de saisie confirmée par l'enseignant."
                  rows={4}
                  className="mt-1 resize-none rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <p className={`mt-1.5 text-[11px] ${motif.length < 10 ? 'text-gray-400' : 'text-teal-600'}`}>
                  {motif.length} / 10 caractères minimum
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4 sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModal(false)}
              className="h-9 min-w-[168px] justify-center gap-2"
            >
              <X className="h-4 w-4" />
              Annuler
            </Button>
            <Button
              onClick={confirmerDeverrouillage}
              disabled={motif.length < 10 || loading}
              className="h-9 min-w-[168px] justify-center gap-2 bg-orange-500 text-white hover:bg-orange-600 disabled:bg-gray-300"
            >
              {loading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                <Unlock className="h-4 w-4" />
              )}
              {loading ? 'Traitement…' : 'Déverrouiller'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// Carte configuration délai
// ============================================
const ConfigDelaiCard = ({ config, onSave, loading }) => {
  const [delai, setDelai] = useState(config.delai_reclamation_jours);
  const [editing, setEditing] = useState(false);

  const sections = {
    primaire: { label: 'Primaire' },
    college: { label: 'Collège' },
    lycee: { label: 'Lycée' }
  };
  const info = sections[config.section] || { label: config.section };

  return (
    <div className={`p-5 border rounded-lg transition-all ${editing ? 'border-[#246990] ring-2 ring-[#246990]/10' : 'border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-4">
        <h4 className="font-semibold text-gray-900">{info.label}</h4>
      </div>
      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Délai (jours)</label>
            <input type="number" min={1} max={14} value={delai}
              onChange={(e) => setDelai(Math.min(14, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-full px-3.5 py-2.5 text-lg font-semibold text-center border border-gray-300 rounded-lg focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-center">Min : 1 · Max : 14 jours</p>
          </div>
          {/* Meme taille, meme largeur : `flex-1` sur l'un et rien sur l'autre
              donnaient un bouton qui mangeait la ligne et un autre a la
              largeur de son texte. DESIGN-SYSTEM.md, section Boutons. */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="h-9 flex-1 justify-center gap-2"
              onClick={() => { setEditing(false); setDelai(config.delai_reclamation_jours); }}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              className="h-9 flex-1 justify-center gap-2 border border-transparent bg-[#246990] text-white hover:bg-[#1a4d6e]"
              onClick={async () => { await onSave(config.id, delai); setEditing(false); }}
              disabled={loading}
            >
              <Save size={14} />
              Enregistrer
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-baseline gap-1.5 mb-4">
            <span className="text-4xl font-bold text-[#246990]">{config.delai_reclamation_jours}</span>
            <span className="text-sm text-gray-500">jours</span>
          </div>
          <Button onClick={() => setEditing(true)} variant="outline" className="w-full py-2.5 border-gray-300 text-gray-700 hover:bg-gray-50">
            Modifier le délai
          </Button>
        </div>
      )}
    </div>
  );
};

export default ParametresVerrouillageNotes;